import React, { useState, useEffect, useRef } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import { cn } from "./ui/utils";

interface AsyncSelectProps {
  value: string | number | null;
  onChange: (id: string | null, label: string, item?: any) => void;
  fetcher: (params: { search: string; per_page: number }) => Promise<any>;
  placeholder: string;
  renderOption?: (item: any) => React.ReactNode;
  getOptionLabel?: (item: any) => string;
  initialDisplay?: string;
  className?: string;
  hasError?: boolean;
}

export function AsyncSelect({
  value,
  onChange,
  fetcher,
  placeholder,
  renderOption,
  getOptionLabel,
  initialDisplay,
  className,
  hasError
}: AsyncSelectProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [selectedDisplay, setSelectedDisplay] = useState<string>(initialDisplay || "");

  useEffect(() => {
    setSelectedDisplay(initialDisplay || "");
  }, [initialDisplay]);

  useEffect(() => {
    if (value === "nenhum" || value === null || value === "") setSelectedDisplay("");
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const fetchOptions = async () => {
      setLoading(true);
      try {
        const response = await fetcher({ search, per_page: 20 });

        let items = [];

        if (Array.isArray(response)) {
          items = response;
        } else if (response?.data) {
          // Caso data.data.data (identificado no log)
          if (response.data.data && Array.isArray(response.data.data.data)) {
            items = response.data.data.data;
          }
          // Caso data.data
          else if (Array.isArray(response.data.data)) {
            items = response.data.data;
          }
          // Caso data simples (array direto no body)
          else if (Array.isArray(response.data)) {
            items = response.data;
          }
        }
        // Caso o próprio objeto de resposta seja o payload e tenha .data que é array
        else if (Array.isArray(response?.data)) {
          items = response.data;
        }

        setOptions(items);
      } catch (error) {
        console.error("AsyncSelect fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    const timeoutId = setTimeout(fetchOptions, search ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [search, open, fetcher]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const getLabel = (item: any) => {
    if (getOptionLabel) return getOptionLabel(item);
    return item.name || item.first_name || item.title || `ID: ${item.id}`;
  };

  return (
    <div ref={ref} className={cn("relative flex-1", className)}>
      <div
        className={cn(
          "flex items-center justify-between w-full h-10 border rounded-md px-3 py-2 bg-white cursor-pointer transition-colors gap-2",
          hasError ? "border-red-500 focus-within:ring-red-500" : "border-gray-300 focus-within:ring-[#D4C070]"
        )}
        onClick={() => setOpen(true)}
      >
        <div className="flex-1 min-w-0">
          {open ? (
            <input
              type="text"
              className="w-full bg-transparent outline-none text-sm"
              placeholder="Buscar..."
              value={search}
              autoFocus
              onChange={(e) => setSearch(e.target.value)}
            />
          ) : (
            <div className={cn("flex flex-wrap gap-2 items-center", (!value || value === "nenhum") ? "text-gray-400" : "text-gray-900")}>
              {value && value !== "nenhum" ? (
                <div className="flex items-center gap-2 overflow-hidden">
                  {selectedDisplay.includes("[") && selectedDisplay.includes("]") ? (
                    <>
                      <span className="bg-gray-800 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-tighter shrink-0">
                        {selectedDisplay.substring(selectedDisplay.indexOf("[") + 1, selectedDisplay.indexOf("]"))}
                      </span>
                      <span className="text-sm font-medium truncate">
                        {selectedDisplay.substring(selectedDisplay.indexOf("]") + 1).split("(")[0].trim()}
                      </span>
                      <span className="text-[10px] text-gray-400 truncate hidden md:inline">
                        {selectedDisplay.includes("(") ? `(${selectedDisplay.split("(")[1]}` : ""}
                      </span>
                    </>
                  ) : (
                    <span className="text-sm font-medium truncate">{selectedDisplay}</span>
                  )}
                </div>
              ) : (
                <span className="text-sm truncate">{placeholder}</span>
              )}
            </div>
          )}
        </div>

        <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200 pointer-events-none", open && "rotate-180")} />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          <div
            className="px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 cursor-pointer italic"
            onMouseDown={() => {
              onChange(null, "");
              setSelectedDisplay("");
              setOpen(false);
            }}
          >
            Limpar seleção
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader2 className="h-4 w-4 animate-spin text-[#D4C070]" />
            </div>
          ) : options.length > 0 ? (
            options.map((item) => (
              <div
                key={item.id}
                className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50"
                onMouseDown={() => {
                  const lbl = getLabel(item);
                  setSelectedDisplay(lbl);
                  onChange(String(item.id), lbl, item);
                  setOpen(false);
                  setSearch("");
                }}
              >
                {renderOption ? renderOption(item) : (
                  <span className="truncate">{getLabel(item)}</span>
                )}
              </div>
            ))
          ) : search.length > 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400 italic text-center p-4">
              Nenhum resultado encontrado
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
